import { OrderableType, Order } from "./orders";
import { BookingType } from "./booking";
import {
  DiscountType,
  DynamicPriceRate,
  FeeLine,
  toDynamicPriceRate,
} from "../offering/api";
import { SpaceType } from "./space";

export interface Quote {
  totalPrice: number;
  orderItems: QuoteOrderItem[];
}

export interface QuoteItem {
  orderableType: string;
  quoteId: string;
  orderableId: string;
  orderable: Orderable;
  unitPrice: number;
  unitPriceAfterDiscount: number;
  unitPriceAfterDiscountWithTax: number;
  unitPriceWithTax: number;
  unitPriceAfterDiscountWithTaxAndFee: number;
  unitPriceWithTaxAndFee: number;
  totalPrice: number;
  quantity: number;
  date: string;
  bundled: boolean;
  subTotal: number;
  unitCount: number;
  // hourly booking
  startTime: string;
  endTime: string;
  // daily booking
  startDate: string;
  endDate: string;
  days: number;
  //original price
  originalUnitPrice: number;
  originalSubTotalPrice: number;
  originalTotalPrice: number;
  quoteItemPrices: QuoteItemPrice[];
  //promotion
  promotion?: Promotion;
  promotionFreeUnit: number;
  promotionAmount: number;
  dynamicPriceRate?: DynamicPriceRate;
  feeLines?: FeeLine[];
}

export interface Promotion {
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  orderNonRefundable: boolean;
}

export function toPromotion(data: any) {
  return {
    code: data.code,
    description: data.description,
    discountType: data.discount_type,
    discountValue: data.discount_value,
    orderNonRefundable: data.order_non_refundable,
  };
}
interface CommonQuoteItemPrice {
  id: string;
  quoteItemId: string;
  originalUnitPrice: number;
  unitPrice: number;
  unitCount: number;
  dynamicPriceRate?: DynamicPriceRate;
  totalPrice: number;
}

export interface DayUnitQuoteItemPrice extends CommonQuoteItemPrice {
  unitType: "day_unit";
  startDate: string;
  endDate: string;
}

interface HourUnitQuoteItemPrice extends CommonQuoteItemPrice {
  unitType: "hour_unit";
  startTime: string;
  endTime: string;
}

export type QuoteItemPrice = DayUnitQuoteItemPrice | HourUnitQuoteItemPrice;

export interface Orderable {
  id: string;
  name: string;
  numberOfEntries: number;
  spaceType: SpaceType;
  displayName: string;
}

export function toOrderable(data: any): Orderable {
  return {
    id: data.id,
    name: data.name,
    numberOfEntries: data.number_of_entries,
    spaceType: data.space_type,
    displayName: data.display_name,
  };
}

export function toQuoteItemPrice(data: any): QuoteItemPrice {
  return {
    id: data.id,
    startDate: data.start_date,
    endDate: data.end_date,
    quoteItemId: data.quote_item_id,
    originalUnitPrice: data.original_unit_price,
    unitCount: data.unit_count,
    unitPrice: data.unit_price,
    unitType: data.unit_type,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
    totalPrice: data.total_price,
    startTime: data.start_time,
    endTime: data.end_time,
  };
}

export interface QuoteOrderItemHourlyBookingDetails {
  attendees: number;
  date: string;
  duration: number;
  spaceID: string;
  time: string;
}

export interface QuoteOrderItemDailyBookingDetails {
  attendees: number;
  startDate: string;
  endDate: string;
  spaceID: string;
  days: number;
}

export function isQuoteOrderItemDailyBookingDetails(
  data: QuoteOrderItemBookingDetails
): data is QuoteOrderItemDailyBookingDetails {
  // @ts-ignore
  if (data.days) {
    return true;
  }

  return false;
}

export type QuoteOrderItemBookingDetails =
  | QuoteOrderItemHourlyBookingDetails
  | QuoteOrderItemDailyBookingDetails;

interface QuoteOrderItemBooking {
  orderableType: OrderableType.Booking;
  unitPrice: number;
  subTotal: number;
  details: QuoteOrderItemBookingDetails;
}

export type QuoteOrderItem = QuoteOrderItemBooking;

export function toQuote(order: Order): Quote {
  return {
    totalPrice: order.totalPrice,
    orderItems: order.orderItems.map((oi) => {
      if (oi.details.bookingType === BookingType.Daily) {
        return {
          orderableType: oi.orderableType,
          unitPrice: oi.unitPrice,
          subTotal: oi.subTotal,
          details: {
            attendees: oi.details.attendees,
            startDate: oi.details.startDate,
            endDate: oi.details.endDate,
            spaceID: oi.details.spaceID,
            days: oi.details.days,
          },
        } as QuoteOrderItemBooking;
      } else {
        return {
          orderableType: oi.orderableType,
          unitPrice: oi.unitPrice,
          subTotal: oi.subTotal,
          details: {
            attendees: oi.details.attendees,
            date: oi.details.date,
            time: oi.details.time,
            duration: Number(oi.details.duration),
            spaceID: oi.details.spaceID,
          },
        } as QuoteOrderItemBooking;
      }
    }),
  };
}
